import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from "axios";
import {
  clearAuth,
  getAuthHeader,
  notifyAuthExpired,
  refreshAccessToken,
} from "./tokenManager";

interface AuthAxiosRequestConfig extends InternalAxiosRequestConfig {
  _withAuth?: boolean;
  _retried?: boolean;
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_VALLARIS,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use(async (config) => {
  const cfg = config as AuthAxiosRequestConfig;
  if (cfg._withAuth === false) return cfg;
  const auth = await getAuthHeader();
  cfg.headers.set("Authorization", auth.Authorization);
  return cfg;
});

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const cfg = error.config as AuthAxiosRequestConfig | undefined;
    const status = error.response?.status;

    if (status === 401 && cfg && cfg._withAuth !== false && !cfg._retried) {
      cfg._retried = true;
      try {
        await refreshAccessToken(true);
      } catch {
        clearAuth();
        notifyAuthExpired();
        return Promise.reject(error);
      }
      return instance.request(cfg);
    }

    const message = error instanceof Error ? error.message : null;
    if (message === "UNAUTHENTICATED" || message === "NO_REFRESH_TOKEN") {
      clearAuth();
      notifyAuthExpired();
    }

    return Promise.reject(error);
  }
);

const buildConfig = (
  withAuth: boolean,
  contentType: string,
  extra: AxiosRequestConfig = {}
): AxiosRequestConfig => {
  const cfg: AxiosRequestConfig & { _withAuth?: boolean } = {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      "Content-Type": contentType,
    },
  };
  if (!withAuth) cfg._withAuth = false;
  return cfg;
};

const get = async <T,>(
  url: string,
  params: any = {},
  withAuth: boolean = true
): Promise<T> => {
  const response = await instance.get<T>(
    url,
    buildConfig(withAuth, "application/json", { params })
  );
  return response.data;
};

const post = async <T,>(
  url: string,
  data: any = {},
  withAuth: boolean = true,
  withUrlendcode: boolean = false,
  withFromData: boolean = false
): Promise<T> => {
  const contentType = withFromData
    ? "multipart/form-data"
    : withUrlendcode
    ? "application/x-www-form-urlencoded"
    : "application/json";
  const response = await instance.post<T>(
    url,
    data,
    buildConfig(withAuth, contentType)
  );
  return response.data;
};

const put = async <T,>(
  url: string,
  data: any = {},
  withAuth: boolean = true
): Promise<T> => {
  const response = await instance.put<T>(
    url,
    data,
    buildConfig(withAuth, "application/json")
  );
  return response.data;
};

const patch = async <T,>(
  url: string,
  data: any = {},
  withAuth: boolean = true
): Promise<T> => {
  const response = await instance.patch<T>(
    url,
    data,
    buildConfig(withAuth, "application/json")
  );
  return response.data;
};

const del = async <T,>(url: string, withAuth: boolean = true): Promise<T> => {
  const response = await instance.delete<T>(
    url,
    buildConfig(withAuth, "application/json")
  );
  return response.data;
};

export const http = {
  get,
  post,
  put,
  patch,
  del,
};
